import session from '@cognitoforms/api/services/session';
import { FeatureFlags } from '@cognitoforms/types/server-types/feature-flags';
import { trackTelemetryEvent } from './telemetry';
import { reportedCurrentPricingTier, resolveUpsellLink } from './journey/registry';
import type { AppInsightsLike } from './telemetry';

export const UPSELL_SHOWN_EVENT = 'Upsell Shown';

export type UpsellOrganization = {
	readonly IsTrial?: boolean;
	readonly Plan?: {
		readonly IsPaidPlan?: boolean;
		readonly PricingTierCode?: string | null;
		readonly Product?: { readonly Name?: string } | null;
	} | null;
} | null | undefined;

const UPSELL_LINK_SELECTOR = 'a[href*="source="]';

const UPSELL_PROMPT_SELECTOR = '[role="dialog"]';

const LEAVING_SELECTOR = '[class*="-leave-active"]';

export const VISIBLE_RATIO = 0.5;

let installed = false;

export function upsellLinksIn(node: Node): Element[] {
	if (!(node instanceof Element))
		return [];

	const links = Array.from(node.querySelectorAll(UPSELL_LINK_SELECTOR));
	if (node.matches(UPSELL_LINK_SELECTOR))
		links.unshift(node);

	return links;
}

export function upsellPromptOf(link: Element): Element {
	return link.closest(UPSELL_PROMPT_SELECTOR) ?? link;
}

function currentPlanDimensions(organization: UpsellOrganization): Record<string, string> {
	const plan = organization?.Plan;
	if (!plan)
		return { CurrentPlan: 'None', CurrentPricingTier: 'None', IsTrial: 'None', IsPaid: 'None' };

	const isTrial = organization.IsTrial === true;
	return {
		CurrentPlan: plan.Product?.Name || 'None',
		CurrentPricingTier: reportedCurrentPricingTier(plan) ?? 'None',
		IsTrial: isTrial ? 'True' : 'False',
		IsPaid: plan.IsPaidPlan && !isTrial ? 'True' : 'False'
	};
}

export function recordUpsellShown(ai: AppInsightsLike | null, link: Element, base: string, organization?: UpsellOrganization): void {
	if (!session.value?.enabledFeatures?.[FeatureFlags.NewBillingPipeline])
		return;

	const start = resolveUpsellLink(link.getAttribute('href'), base);
	if (!start)
		return;

	trackTelemetryEvent(ai, UPSELL_SHOWN_EVENT, {
		OrganizationId: session.value?.organizationId ?? '',
		Variant: start.variant,
		EntryPoint: start.entryPoint ?? 'None',
		Detail: start.detail ?? 'None',
		...currentPlanDimensions(organization)
	}, {});
}

export function trackUpsellImpressions(root: Element, getAppInsights: () => AppInsightsLike | null, base: () => string, getOrganization: () => UpsellOrganization): { disconnect: () => void } {
	const watched = new Set<Element>();
	const visible = new Map<Element, Element>();
	const shown = new Set<Element>();

	const hide = (link: Element) => {
		const prompt = visible.get(link);
		if (!prompt)
			return;

		visible.delete(link);
		if (!Array.from(visible.values()).includes(prompt))
			shown.delete(prompt);
	};

	const visibility = new IntersectionObserver(entries => {
		for (const entry of entries) {
			const link = entry.target;
			if (!entry.isIntersecting || entry.intersectionRatio < VISIBLE_RATIO || link.closest(LEAVING_SELECTOR)) {
				hide(link);
				continue;
			}

			const prompt = upsellPromptOf(link);
			visible.set(link, prompt);
			if (shown.has(prompt))
				continue;

			shown.add(prompt);
			recordUpsellShown(getAppInsights(), link, base(), getOrganization());
		}
	}, { threshold: VISIBLE_RATIO });

	const isUpsell = (link: Element) => resolveUpsellLink(link.getAttribute('href'), base()) !== null;

	const watch = (link: Element) => {
		if (watched.has(link) || !isUpsell(link))
			return;

		watched.add(link);
		visibility.observe(link);
	};

	const forget = (link: Element) => {
		if (!watched.delete(link))
			return;

		visibility.unobserve(link);
		hide(link);
	};

	const offerChanged = (link: Element) => {
		if (watched.has(link))
			shown.delete(upsellPromptOf(link));
		forget(link);
		watch(link);
	};

	upsellLinksIn(root).forEach(watch);

	const changes = new MutationObserver(records => {
		for (const record of records) {
			if (record.type === 'attributes') {
				if (record.target instanceof Element)
					offerChanged(record.target);
				continue;
			}

			record.removedNodes.forEach(node => {
				for (const link of upsellLinksIn(node)) {
					if (!link.isConnected)
						forget(link);
				}
			});
			record.addedNodes.forEach(node => upsellLinksIn(node).forEach(watch));
		}
	});
	changes.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });

	return {
		disconnect: () => {
			changes.disconnect();
			visibility.disconnect();
			watched.clear();
			visible.clear();
			shown.clear();
		}
	};
}

export function installUpsellImpressions(getAppInsights: () => AppInsightsLike | null, getOrganization: () => UpsellOrganization): void {
	if (installed || typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined' || !document.body)
		return;
	installed = true;

	trackUpsellImpressions(document.body, getAppInsights, () => location.href, getOrganization);
}
